import type { JobRecord, JobStatus } from "@focalfolio/domain-model";

export interface JobStore {
  putJob(job: JobRecord): Promise<void>;
  getJobs(): Promise<JobRecord[]>;
  getRecoverableJobs(): Promise<JobRecord[]>;
}

export interface JobContext<TPayload = unknown> {
  job: JobRecord<TPayload>;
  signal: AbortSignal;
  update: (change: Partial<Pick<JobRecord, "status" | "progress" | "completedItems" | "totalItems" | "message" | "error">>) => Promise<void>;
}

export type JobHandler<TPayload = unknown> = (context: JobContext<TPayload>) => Promise<void>;

export class DurableJobRunner {
  private readonly handlers = new Map<string, JobHandler>();
  private readonly controllers = new Map<string, AbortController>();

  constructor(private readonly store: JobStore) {}

  register<TPayload>(type: string, handler: JobHandler<TPayload>): void {
    this.handlers.set(type, handler as JobHandler);
  }

  async create<TPayload>(type: string, payload: TPayload, totalItems: number, message = "Ready"): Promise<JobRecord<TPayload>> {
    const now = Date.now();
    const job: JobRecord<TPayload> = {
      id: `job_${crypto.randomUUID()}`,
      type,
      status: "planned",
      progress: 0,
      completedItems: 0,
      totalItems,
      message,
      payload,
      createdAt: now,
      updatedAt: now
    };
    await this.store.putJob(job);
    return job;
  }

  async run<TPayload>(job: JobRecord<TPayload>): Promise<JobRecord<TPayload>> {
    const handler = this.handlers.get(job.type);
    if (!handler) throw new Error(`No job handler is registered for ${job.type}.`);
    if (this.controllers.has(job.id)) throw new Error(`Job ${job.id} is already running.`);
    const controller = new AbortController();
    this.controllers.set(job.id, controller);
    let current: JobRecord<TPayload> = { ...job, status: "running" as JobStatus, updatedAt: Date.now() };
    delete current.error;
    await this.store.putJob(current);

    const update = async (change: Partial<JobRecord>): Promise<void> => {
      current = { ...current, ...change, updatedAt: Date.now() } as JobRecord<TPayload>;
      current.progress = Math.max(0, Math.min(1, current.progress));
      await this.store.putJob(current);
    };

    try {
      await handler({ job: current, signal: controller.signal, update });
      if (controller.signal.aborted) {
        await update({ status: "cancelled", message: "Cancelled" });
      } else {
        await update({ status: "completed", progress: 1, completedItems: current.totalItems, message: "Completed" });
      }
    } catch (error) {
      if (controller.signal.aborted) {
        await update({ status: "cancelled", message: "Cancelled" });
      } else {
        await update({
          status: "failed",
          message: "Failed",
          error: error instanceof Error ? error.message : String(error)
        });
      }
    } finally {
      this.controllers.delete(job.id);
    }
    return current;
  }

  cancel(jobId: string): void {
    this.controllers.get(jobId)?.abort(new DOMException("Cancelled by user", "AbortError"));
  }

  async recover(): Promise<JobRecord[]> {
    const jobs = await this.store.getRecoverableJobs();
    const recovered: JobRecord[] = [];
    for (const job of jobs) {
      const next = {
        ...job,
        status: "needs-user-action" as const,
        message: "Interrupted operation: review and resume or discard.",
        updatedAt: Date.now()
      };
      await this.store.putJob(next);
      recovered.push(next);
    }
    return recovered;
  }
}

export async function runBoundedBatch<T>(input: {
  items: T[];
  concurrency?: number;
  signal?: AbortSignal;
  worker: (item: T, index: number) => Promise<void>;
  onProgress?: (completed: number, total: number) => Promise<void> | void;
}): Promise<void> {
  const concurrency = Math.max(1, Math.min(input.concurrency ?? 3, 12));
  let cursor = 0;
  let completed = 0;
  const runners = Array.from({ length: Math.min(concurrency, input.items.length) }, async () => {
    while (true) {
      if (input.signal?.aborted) throw input.signal.reason ?? new DOMException("Aborted", "AbortError");
      const index = cursor;
      cursor += 1;
      if (index >= input.items.length) return;
      await input.worker(input.items[index] as T, index);
      completed += 1;
      await input.onProgress?.(completed, input.items.length);
    }
  });
  await Promise.all(runners);
}
